export const TTORIA_ASYNC_FEEDBACK_LEGACY = 'legacy';
export const TTORIA_ASYNC_FEEDBACK_V1 = 'ttoria-saas-async-feedback-v1';
export const TTORIA_ASYNC_FEEDBACK_EVENT = 'ttoria.async_feedback.v1';

export const TTORIA_ASYNC_FEEDBACK_TIMING = Object.freeze({
  localFeedbackBudgetMs: 100,
  railDelayMs: 1000,
  supplementarySuccessMs: 1800,
  longRunningMs: 10000
});

export const TTORIA_ASYNC_FEEDBACK_OP_CLASSES = Object.freeze([
  'route-transition',
  'user-mutation',
  'foreground-read',
  'background-refresh'
]);

export const TTORIA_ASYNC_FEEDBACK_OP_PRIORITY = Object.freeze({
  'background-refresh': 1,
  'foreground-read': 2,
  'user-mutation': 3,
  'route-transition': 4
});

const TARGETS_BY_APP = Object.freeze({
  'tutor-desk': Object.freeze(['saas-staging', 'saas-production']),
  student: Object.freeze(['saas-staging', 'saas-production']),
  'ttoria-control-centre': Object.freeze(['saas-staging', 'saas-production'])
});

function clean(value = '') {
  return String(value ?? '').trim();
}

function immutable(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) immutable(child);
  return Object.freeze(value);
}

export function isTtoriaAsyncFeedbackTuple(appId = '', target = '') {
  const normalizedAppId = clean(appId);
  const normalizedTarget = clean(target);
  return Boolean(TARGETS_BY_APP[normalizedAppId]?.includes(normalizedTarget));
}

export function resolveTtoriaAsyncFeedbackConfig({
  standard = '',
  appId = '',
  target = '',
  expectedAppId = '',
  buildId = '',
  artifactSha256 = ''
} = {}) {
  const normalizedStandard = clean(standard);
  const normalizedAppId = clean(appId);
  const normalizedTarget = clean(target);
  const normalizedExpectedAppId = clean(expectedAppId);
  const tupleValid = isTtoriaAsyncFeedbackTuple(normalizedAppId, normalizedTarget);
  const appMatches = !normalizedExpectedAppId || normalizedExpectedAppId === normalizedAppId;
  const configuredStandardValid = normalizedStandard === TTORIA_ASYNC_FEEDBACK_LEGACY
    || normalizedStandard === TTORIA_ASYNC_FEEDBACK_V1;
  const enabled = configuredStandardValid
    && normalizedStandard === TTORIA_ASYNC_FEEDBACK_V1
    && tupleValid
    && appMatches;
  const reason = enabled
    ? 'enabled-exact-server-tuple'
    : !configuredStandardValid
      ? 'missing-or-invalid-standard'
      : !tupleValid
        ? 'invalid-app-target-tuple'
        : !appMatches
          ? 'app-mismatch'
          : 'configured-legacy';

  return immutable({
    contractId: TTORIA_ASYNC_FEEDBACK_V1,
    standard: enabled ? TTORIA_ASYNC_FEEDBACK_V1 : TTORIA_ASYNC_FEEDBACK_LEGACY,
    enabled,
    authoritative: configuredStandardValid && tupleValid && appMatches,
    appId: tupleValid && appMatches ? normalizedAppId : '',
    target: tupleValid && appMatches ? normalizedTarget : '',
    buildId: clean(buildId).slice(0, 128),
    artifactSha256: /^[a-f0-9]{64}$/u.test(clean(artifactSha256).toLowerCase())
      ? clean(artifactSha256).toLowerCase()
      : '',
    reason
  });
}

export function resolveTtoriaAsyncFeedbackEnv(env = {}, {
  appId = '',
  targetKey = ''
} = {}) {
  const safeEnv = env && typeof env === 'object' ? env : {};
  return resolveTtoriaAsyncFeedbackConfig({
    standard: safeEnv.TTORIA_ASYNC_FEEDBACK_STANDARD,
    appId,
    target: safeEnv[targetKey]
  });
}

export function toTtoriaAsyncFeedbackSessionPayload(config = {}) {
  const resolved = resolveTtoriaAsyncFeedbackConfig(config);
  return immutable({
    contractId: resolved.contractId,
    standard: resolved.standard,
    enabled: resolved.enabled,
    appId: resolved.appId,
    target: resolved.target,
    buildId: resolved.buildId,
    artifactSha256: resolved.artifactSha256
  });
}

export const TTORIA_ASYNC_FEEDBACK_TARGETS_BY_APP = TARGETS_BY_APP;
